// ── the hero netti's look ────────────────────────────────────────────────────
//
// John's /netti export (2026-09-30, scripts/og-cards/netti/hero-netti.settings.json),
// the one the root cards are built on: its formation, petals, glass, veins,
// nodes and motes verbatim, in the door's blue, cyan and violet, with PRESENCE
// (the paper creature kept on a deep ground). It lived inside the auth stage;
// it lives here now so every room that wears THIS netti (the door, the
// developers page) wears the same creature, never a copy that drifts.

import type { ColorState, RGB } from "./color";
import type { FutureTuners } from "./future/futureOrganism";

export const HERO_NETTI_LOOK: {
  tuners: Partial<FutureTuners>;
  age: number;
  spin: number;
  presence: number;
  colors: [string, string, string];
  contrast: number;
} = {
  tuners: {
    formation: 0.715, radialBias: 0.368, abundance: 1, cellSize: 0,
    petalLength: 0.616, petalWidth: 0.33, tipSharpness: 0.427, bend: 0.454,
    variety: 0.537, glass: 0.255, venation: 0.618, nodes: 0.341, innerLight: 0.267,
    hueSpread: 0.446, revolve: 0.62, sway: 0.279, tempo: 0.171, breath: 0.383,
    motes: 0.545, moteSize: 0.135, touch: 0,
  },
  age: 0.882,
  spin: 30,
  // the paper creature, kept on this ground (FutureOrganism.setPresence)
  presence: 1,
  colors: ["#eac72a", "#37c8e6", "#7c43e5"],
  contrast: 0.74,
};

export function hexToHsl(hex: string): { hue: number; sat: number; light: number } {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min;
  let h = 0, s = 0;
  if (d > 1e-5) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { hue: (h + 360) % 360, sat: s, light: l };
}

export const HERO_NETTI_COLOR: ColorState = {
  primary: hexToHsl(HERO_NETTI_LOOK.colors[0]),
  secondary: hexToHsl(HERO_NETTI_LOOK.colors[1]),
  tinge: hexToHsl(HERO_NETTI_LOOK.colors[2]),
  contrast: HERO_NETTI_LOOK.contrast,
};

/** the key blue the hero netti is lit against (≈ hsl(210 73% 44%)) */
export const HERO_NETTI_GROUND: RGB = [0.12, 0.43, 0.76];

// ── the developers page's netti ──────────────────────────────────────────────
//
// The reference image's netti (/tmp/claude-501/am/ref-netti-teal.jpg, 2026-10-12):
// the SAME creature as the hero look (its formation, whorls and breath), but a
// bloom of glassy facets in pale green laid over a darker green body, broad
// smooth veins instead of hair, faint round cells, and a big white heart. It
// stands on a teal-to-green ground (#0b5d66 over #12895e), and lives here beside
// the hero look without ever editing it: the signup door, the kit's `hero` look
// and the og cards all pin HERO_NETTI_LOOK.
//
// Found by eye, against the image, on the page's own ground:
//  - the body is the landing bloom's (wide soft petals, innerLight high) pushed
//    toward the reference: broader and longer petals (width .5, length .72) with a
//    sharper tip (.55), so they overlap as pale translucent facets and not as a
//    blur; the hair veins are turned down (venation .2) to the broad curved
//    bands the image has.
//  - the colour is a leaf green, a deep green and a pale lime-white, at contrast
//    .55. Pigments multiply: a dark outer whorl at higher contrast goes to
//    black, a white fringe at lower washes the body.
//  - the cells (John: "more visible cells"): motes .8 at moteSize .03 and the
//    vein-tip nodes at .2. Nodes at .3 and above draw flat confetti discs the
//    image does not have; below .2 the cells vanish.
//  - cellSize .6 gives the heart the size it has in the image (about a sixth of
//    the width at the hero seat); cellGlow stays 0, because on a creature with
//    petals it washes the whole bloom out to a pale cloud (it is for a single
//    resting cell).
//  - bg is the hero's own teal-green, so the petals' thin washes tint toward the
//    ground and fade out into it, never into a grey.

export const DEV_NETTI_LOOK: {
  tuners: Partial<FutureTuners>;
  age: number;
  spin: number;
  presence: number;
  colors: [string, string, string];
  contrast: number;
} = {
  tuners: {
    formation: 0.864, radialBias: 0.644, abundance: 0.866, cellSize: 0.6,
    petalLength: 0.72, petalWidth: 0.5, tipSharpness: 0.55, bend: 0.34, variety: 0.2,
    glass: 0.3, venation: 0.2, nodes: 0.2, innerLight: 0.65, hueSpread: 0.446,
    revolve: 0.3, sway: 0.5, tempo: 0.45, breath: 0.4, motes: 0.8, moteSize: 0.03, touch: 0,
  },
  age: 0.9,
  spin: 30,
  presence: 1,
  colors: ["#6ab35a", "#1d6b4a", "#e6f7d8"],
  contrast: 0.55,
};

export const DEV_NETTI_COLOR: ColorState = {
  primary: hexToHsl(DEV_NETTI_LOOK.colors[0]),
  secondary: hexToHsl(DEV_NETTI_LOOK.colors[1]),
  tinge: hexToHsl(DEV_NETTI_LOOK.colors[2]),
  contrast: DEV_NETTI_LOOK.contrast,
};

/** the teal-green its thin washes are tinted from: the hero's own ground (≈ #12735f) */
export const DEV_NETTI_GROUND: RGB = [0.07, 0.45, 0.38];
